import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * StoryNavSml - Compact Story Map Navigation Component (Standard Size)
 * 
 * DYNAMIC VIEW CRITERIA:
 * - Horizontal tree layout with drill-down navigation
 * - White drop shadow on nodes for visibility on any background
 * - 30% larger than original compact size
 * - Accepts initialFocus prop to auto-focus a specific branch
 * - Accepts siblingFontScale prop to adjust unfocused sibling font size
 */
const StoryNavSml = ({ initialFocus = null, siblingFontScale = 1.5 }) => {
  const navigate = useNavigate();
  const [hoveredNode, setHoveredNode] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  
  const [focusedBranch, setFocusedBranch] = useState(() => {
    // If initialFocus is provided, use it; otherwise check sessionStorage
    if (initialFocus) return initialFocus;
    const saved = sessionStorage.getItem('storyMapFocusedBranch');
    return saved || null;
  });

  React.useEffect(() => {
    // If initialFocus is provided, always use it (page-specific focus)
    if (initialFocus && focusedBranch !== initialFocus) {
      setFocusedBranch(initialFocus);
    }
  }, [initialFocus]);

  React.useEffect(() => {
    if (focusedBranch) {
      sessionStorage.setItem('storyMapFocusedBranch', focusedBranch);
    } else {
      sessionStorage.removeItem('storyMapFocusedBranch');
    }
  }, [focusedBranch]);

  // Standard node dimensions (30% larger than compact)
  const NODE_WIDTH = 182;
  const NODE_HEIGHT = 49;
  const CHILD_NODE_WIDTH = 146;
  const CHILD_NODE_HEIGHT = 36;
  const H_GAP = 36;
  const V_GAP = 5;

  const branches = [
    {
      id: 'explore',
      label: 'Checking things out',
      color: '#059669',
      path: '/features',
      children: [
        { id: 'explore-overview', label: 'Overview', color: '#047857', path: '/features' },
        { id: 'explore-tiers', label: 'Paid Tier Options', color: '#10b981', path: '/pricing' }
      ]
    },
    {
      id: 'diy',
      label: 'I have a Twee for my website (DIY)',
      color: '#4f46e5',
      path: '/examples',
      children: [
        { id: 'diy-overview', label: 'Overview', color: '#4338ca', path: '/examples' },
        { id: 'diy-samples', label: 'Sample Stories', color: '#6366f1', path: '/examples' },
        { id: 'diy-compiler', label: 'Easy Story Builder', color: '#6366f1', path: '/compiler' }
      ]
    },
    {
      id: 'pro',
      label: 'Help building better stories (PRO)',
      color: '#9333ea',
      path: '/pro',
      children: [
        { id: 'pro-overview', label: 'Overview', color: '#7e22ce', path: '/pro' },
        { id: 'pro-ve', label: 'Visual Editor', color: '#a855f7', path: '/visual-editor?starter=true' },
        { id: 'pro-vsp', label: 'Visual Story Planner', color: '#a855f7', path: '/visual-story-planner' },
        { id: 'pro-shared', label: 'Shared Story (Beta)', color: '#a855f7', path: '/compiler' }
      ]
    },
    {
      id: 'agency',
      label: 'Clients, Advanced CMS (Agency)',
      color: '#d97706',
      path: '/agency',
      children: [
        { id: 'agency-overview', label: 'Overview', color: '#b45309', path: '/agency' },
        { id: 'agency-sitemap', label: 'Sitemap to PathNav', color: '#f59e0b', path: '/sitemap-to-pathnav' },
        { id: 'agency-journey', label: 'Journey Wizard', color: '#f59e0b', path: '/journey-wizard' },
        { id: 'agency-pro', label: 'All PRO Features', color: '#f59e0b', path: '/pro' }
      ]
    }
  ];

  const startNode = {
    id: 'start',
    label: 'Click to visually view interest areas',
    color: '#64748b'
  };

  const calculatePositions = () => {
    const nodes = [];
    const edges = [];
    const startX = 10;

    // Collapsed state — only the root node is shown
    if (!isExpanded) {
      nodes.push({
        ...startNode,
        x: startX, y: 10,
        width: NODE_WIDTH, height: NODE_HEIGHT,
        scale: 1, isStart: true, isFocused: true
      });
      return { nodes, edges };
    }

    if (focusedBranch === null) {
      const startY = 10;
      const branchX = startX + NODE_WIDTH + H_GAP;
      let branchY = startY;
      
      nodes.push({
        ...startNode,
        x: startX, y: startY,
        width: NODE_WIDTH, height: NODE_HEIGHT,
        scale: 1, isStart: true, isFocused: true
      });
      
      branches.forEach((branch) => {
        nodes.push({
          ...branch,
          x: branchX, y: branchY,
          width: NODE_WIDTH, height: NODE_HEIGHT,
          scale: 1, isBranch: true,
          hasChildren: branch.children && branch.children.length > 0
        });
        
        edges.push({
          from: { x: startX + NODE_WIDTH, y: startY + NODE_HEIGHT / 2 },
          to: { x: branchX, y: branchY + NODE_HEIGHT / 2 }
        });
        
        branchY += NODE_HEIGHT + V_GAP;
      });
    } else {
      const focusedBranchData = branches.find(b => b.id === focusedBranch);
      const focusedChildren = focusedBranchData?.children || [];
      const unfocusedScale = 0.5;
      const unfocusedHeight = NODE_HEIGHT * unfocusedScale;
      const unfocusedWidth = NODE_WIDTH * unfocusedScale;
      
      const startY = 10;
      const branchX = startX + NODE_WIDTH + H_GAP;
      
      nodes.push({
        ...startNode,
        x: startX, y: startY,
        width: NODE_WIDTH, height: NODE_HEIGHT,
        scale: 1, isStart: true, isFocused: false
      });
      
      const childX = branchX + NODE_WIDTH + H_GAP;
      let branchY = startY;
      let childStartY = startY;
      
      branches.forEach((branch) => {
        const isFocused = branch.id === focusedBranch;
        const scale = isFocused ? 1 : unfocusedScale;
        const height = isFocused ? NODE_HEIGHT : unfocusedHeight;
        const width = isFocused ? NODE_WIDTH : unfocusedWidth;
        
        nodes.push({
          ...branch,
          x: branchX, y: branchY,
          width: width, height: height,
          scale: scale, isBranch: true, isFocused: isFocused,
          hasChildren: !isFocused && branch.children && branch.children.length > 0
        });
        
        edges.push({
          from: { x: startX + NODE_WIDTH, y: startY + NODE_HEIGHT / 2 },
          to: { x: branchX, y: branchY + height / 2 },
          dimmed: !isFocused
        });
        
        if (isFocused && focusedChildren.length > 0) {
          let childY = childStartY;
          
          focusedChildren.forEach((child) => {
            nodes.push({
              ...child,
              x: childX, y: childY,
              width: CHILD_NODE_WIDTH, height: CHILD_NODE_HEIGHT,
              scale: 1, isChild: true
            });
            
            edges.push({
              from: { x: branchX + width, y: branchY + height / 2 },
              to: { x: childX, y: childY + CHILD_NODE_HEIGHT / 2 }
            });
            
            childY += CHILD_NODE_HEIGHT + V_GAP;
          });
        }
        
        branchY += height + V_GAP;
      });
    }
    
    return { nodes, edges };
  };

  const { nodes, edges } = calculatePositions();

  const handleNodeClick = (node) => {
    if (node.isStart) {
      if (!isExpanded) {
        setIsExpanded(true);
      } else {
        setFocusedBranch(null);
        setIsExpanded(false);
      }
    } else if (node.isBranch) {
      if (node.isFocused) {
        setFocusedBranch(null);
      } else {
        setFocusedBranch(node.id);
      }
    } else if (node.isChild && node.path) {
      navigate(node.path);
    }
  };

  const maxX = Math.max(...nodes.map(n => n.x + n.width)) + 15;
  const maxY = Math.max(...nodes.map(n => n.y + n.height)) + 15;
  const svgWidth = Math.max(450, maxX);
  const svgHeight = Math.max(230, maxY);

  // Standard font rendering - larger fonts for siblings
  const renderLabel = (node) => {
    const baseFontSize = node.isChild ? 10 : 11;
    // For unfocused siblings, use siblingFontScale prop (default 1.3 = 30% larger)
    const fontScale = node.scale < 1 ? Math.max(node.scale * siblingFontScale, 0.65) : (node.scale || 1);
    const fontSize = baseFontSize * fontScale;
    const maxCharsPerLine = node.scale < 1 ? 12 : (node.isChild ? 14 : 18);
    
    const words = node.label.split(' ');
    let lines = [];
    let currentLine = '';
    
    words.forEach(word => {
      if ((currentLine + ' ' + word).trim().length <= maxCharsPerLine) {
        currentLine = (currentLine + ' ' + word).trim();
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
    if (currentLine) lines.push(currentLine);
    
    if (node.scale && node.scale < 1 && lines.length > 2) {
      lines = lines.slice(0, 2);
      lines[1] = lines[1].substring(0, 7) + '...';
    }
    
    if (lines.length > 1) {
      const lineHeight = fontSize * 1.15;
      const totalTextHeight = lines.length * lineHeight;
      const startY = (node.height - totalTextHeight) / 2 + fontSize * 0.8;
      
      return (
        <text x={node.width / 2} y={startY} textAnchor="middle" fill="white" fontSize={fontSize} fontWeight="600" style={{ pointerEvents: 'none' }}>
          {lines.map((line, i) => (<tspan key={i} x={node.width / 2} dy={i === 0 ? 0 : lineHeight}>{line}</tspan>))}
        </text>
      );
    }
    
    return (
      <text x={node.width / 2} y={node.height / 2} textAnchor="middle" dominantBaseline="middle" fill="white" fontSize={fontSize} fontWeight="600" style={{ pointerEvents: 'none' }}>
        {node.label}
      </text>
    );
  };

  // ── Collapsed state: full-width single-row button, no SVG, no whitespace gap ──
  if (!isExpanded) {
    return (
      <div className="w-full">
        <button
          type="button"
          data-testid="storynav-collapsed-btn"
          onClick={() => setIsExpanded(true)}
          className="w-full flex items-center justify-between px-6 py-4 bg-indigo-50 hover:bg-indigo-100 transition-colors rounded-full border border-indigo-200 text-left"
        >
          <div className="flex flex-col items-start">
            <span className="text-sm font-semibold text-indigo-800">Interactive Site Map</span>
            <span className="text-xs text-indigo-500">(Story Builder In Action)</span>
          </div>
          <ChevronDown className="h-4 w-4 text-indigo-600 flex-shrink-0 ml-2" />
        </button>
      </div>
    );
  }

  // ── Expanded state: full interactive SVG mind map ──────────────────────────
  return (
    <div className="w-full">
      {/* Collapse trigger above the map */}
      <button
        type="button"
        data-testid="storynav-collapse-btn"
        onClick={() => { setIsExpanded(false); setFocusedBranch(null); }}
        className="w-full flex items-center justify-between px-6 py-4 bg-indigo-50 hover:bg-indigo-100 transition-colors rounded-full border border-indigo-200 text-left mb-1"
      >
        <div className="flex flex-col items-start">
          <span className="text-sm font-semibold text-indigo-800">Interactive Site Map</span>
          <span className="text-xs text-indigo-500">(Story Builder In Action)</span>
        </div>
        <ChevronUp className="h-4 w-4 text-indigo-600 flex-shrink-0 ml-2" />
      </button>
      <div className="flex justify-start overflow-x-auto">
        <svg width={svgWidth} height={svgHeight} viewBox={`0 0 ${svgWidth} ${svgHeight}`}>
          {/* Drop shadow filter */}
          <defs>
            <filter id="dropShadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="white" floodOpacity="0.5"/>
            </filter>
          </defs>
          
          {edges.map((edge, idx) => (
            <path
              key={idx}
              d={`M ${edge.from.x} ${edge.from.y} C ${edge.from.x + 22} ${edge.from.y}, ${edge.to.x - 22} ${edge.to.y}, ${edge.to.x} ${edge.to.y}`}
              fill="none" stroke="#94a3b8" strokeWidth="2.5" opacity={edge.dimmed ? 0.6 : 1}
            />
          ))}
          {nodes.map((node) => (
            <g
              key={node.id}
              transform={`translate(${node.x}, ${node.y})`}
              onClick={() => handleNodeClick(node)}
              onMouseEnter={() => setHoveredNode(node.id)}
              onMouseLeave={() => setHoveredNode(null)}
              style={{ cursor: 'pointer' }}
              filter="url(#dropShadow)"
            >
              {(node.isFocused || (node.isStart && focusedBranch === null)) && (
                <rect x="-3" y="-3" width={node.width + 6} height={node.height + 6} rx="8" fill="none" stroke="#f97316" strokeWidth="2.5" />
              )}
              <rect
                width={node.width} height={node.height} rx={node.scale < 1 ? 4 : 6}
                fill={node.color} opacity={hoveredNode === node.id ? 1 : (node.scale < 1 ? 0.7 : 0.95)}
                className="transition-all duration-200"
                style={{ filter: hoveredNode === node.id ? 'brightness(1.1)' : 'none' }}
              />
              {node.hasChildren && !node.isFocused && node.scale === 1 && (
                <text x={node.width - 12} y={node.height / 2} textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="12" opacity="0.7">→</text>
              )}
              {renderLabel(node)}
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
};

export default StoryNavSml;
